import React, { useState } from "react";
import { Lock, KeyRound, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { unwrapVaultKey, wrapVaultKey, normalizeRecoveryCode, KDF_ITERATIONS } from "@/lib/vaultCrypto";
import { toast } from "sonner";

export default function VaultUnlock({ vault, onUnlocked }) {
  const [mode, setMode] = useState("passphrase");
  const [pass, setPass] = useState("");
  const [code, setCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newConfirm, setNewConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const unlockWithPassphrase = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const key = await unwrapVaultKey(pass, vault.kdf_salt, vault.wrapped_key_iv, vault.wrapped_key, vault.kdf_iterations);
      onUnlocked(key);
    } catch {
      setError("That passphrase doesn't open this vault.");
    } finally {
      setBusy(false);
    }
  };

  const recover = async (e) => {
    e.preventDefault();
    setError("");
    if (newPass.length < 10) return setError("New passphrase must be at least 10 characters.");
    if (newPass !== newConfirm) return setError("New passphrases don't match.");
    setBusy(true);
    try {
      const key = await unwrapVaultKey(normalizeRecoveryCode(code), vault.recovery_salt, vault.recovery_wrapped_key_iv, vault.recovery_wrapped_key, vault.kdf_iterations);
      const p = await wrapVaultKey(key, newPass);
      await api.put("/vault/keys", { kdf_salt: p.salt, kdf_iterations: KDF_ITERATIONS, wrapped_key: p.wrapped, wrapped_key_iv: p.iv });
      toast.success("Passphrase reset. Your recovery code still works — keep it safe.");
      onUnlocked(key);
    } catch (err) {
      setError(err.response ? "Could not save the new passphrase." : "That recovery code doesn't match this vault.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="vault-unlock">
      <div className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-8 shadow-xl shadow-slate-900/5">
        <div className="h-12 w-12 rounded-xl bg-slate-900 flex items-center justify-center mb-4">
          {mode === "passphrase" ? <Lock size={22} className="text-emerald-400" /> : <KeyRound size={22} className="text-emerald-400" />}
        </div>
        {mode === "passphrase" ? (
          <form onSubmit={unlockWithPassphrase} className="space-y-4">
            <div>
              <h1 className="font-display text-3xl text-slate-900" style={{ fontWeight: 800 }}>Unlock your vault</h1>
              <p className="text-sm text-slate-500 mt-1">{vault.entry_count} {vault.entry_count === 1 ? "entry" : "entries"} · encrypted end-to-end</p>
            </div>
            <div>
              <Label htmlFor="unlock-pass">Vault passphrase</Label>
              <Input id="unlock-pass" data-testid="vault-unlock-input" type="password" value={pass} onChange={(e) => setPass(e.target.value)} className="mt-1" autoFocus autoComplete="current-password" />
            </div>
            {error && <p data-testid="vault-unlock-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</p>}
            <Button data-testid="vault-unlock-btn" type="submit" disabled={!pass || busy} className="w-full h-11 bg-slate-900 hover:bg-emerald-600 rounded-full">
              {busy ? "Unlocking..." : "Unlock"}
            </Button>
            {vault.has_recovery && (
              <button type="button" data-testid="vault-forgot-btn" onClick={() => { setMode("recover"); setError(""); }} className="text-sm text-slate-500 hover:text-emerald-700 underline w-full text-center">
                Forgot your passphrase? Use your recovery code
              </button>
            )}
          </form>
        ) : (
          <form onSubmit={recover} className="space-y-4">
            <div>
              <h1 className="font-display text-3xl text-slate-900" style={{ fontWeight: 800 }}>Recover with your code</h1>
              <p className="text-sm text-slate-500 mt-1">Enter the recovery code you saved when creating the vault, then choose a new passphrase.</p>
            </div>
            <div>
              <Label htmlFor="rec-code">Recovery code</Label>
              <Input id="rec-code" data-testid="vault-recovery-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" className="mt-1 font-mono uppercase" autoFocus />
            </div>
            <div>
              <Label htmlFor="rec-new">New passphrase</Label>
              <Input id="rec-new" data-testid="vault-recovery-newpass-input" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="rec-confirm">Confirm new passphrase</Label>
              <Input id="rec-confirm" data-testid="vault-recovery-confirm-input" type="password" value={newConfirm} onChange={(e) => setNewConfirm(e.target.value)} className="mt-1" />
            </div>
            {error && <p data-testid="vault-recovery-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</p>}
            <Button data-testid="vault-recover-btn" type="submit" disabled={!code || !newPass || busy} className="w-full h-11 bg-slate-900 hover:bg-emerald-600 rounded-full">
              {busy ? "Recovering..." : "Reset passphrase & unlock"}
            </Button>
            <button type="button" onClick={() => { setMode("passphrase"); setError(""); }} className="text-sm text-slate-500 hover:text-emerald-700 w-full text-center flex items-center justify-center gap-1">
              <ArrowLeft size={14} /> Back to passphrase
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
